import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText,
  Edit3,
  Scissors,
  CheckCircle2,
  Filter,
  Zap,
  BookOpen,
  Sparkles,
  AlignLeft
} from 'lucide-react';

const RewriteGenerationAnimation = () => {
  const stages = [
    {
      id: 'reading',
      title: 'Reading Regulation',
      subtitle: 'Parsing regulatory text and structure...',
      icon: BookOpen,
      color: '#8b5cf6',
      duration: 2000
    },
    {
      id: 'analyzing',
      title: 'Analyzing Provisions',
      subtitle: 'Identifying mandatory and discretionary requirements...',
      icon: FileText,
      color: '#6366f1',
      duration: 2500
    },
    {
      id: 'filtering',
      title: 'Removing Unauthorized',
      subtitle: 'Eliminating provisions without statutory authority...',
      icon: Filter,
      color: '#ef4444',
      duration: 3000
    },
    {
      id: 'simplifying',
      title: 'Simplifying Language',
      subtitle: 'Converting legal jargon to plain English...',
      icon: Edit3,
      color: '#3b82f6',
      duration: 3000
    },
    {
      id: 'condensing',
      title: 'Removing Redundancies',
      subtitle: 'Eliminating duplicate and unnecessary text...',
      icon: Scissors,
      color: '#f59e0b',
      duration: 2500
    },
    {
      id: 'optimizing',
      title: 'Optimizing Structure',
      subtitle: 'Reorganizing for clarity and brevity...',
      icon: AlignLeft,
      color: '#10b981',
      duration: 2000
    },
    {
      id: 'finalizing',
      title: 'Finalizing Draft',
      subtitle: 'Applying finishing touches and formatting...',
      icon: Sparkles,
      color: '#a855f7',
      duration: 2000
    },
    {
      id: 'complete',
      title: 'Rewrite Complete',
      subtitle: 'Plain language version ready for review',
      icon: CheckCircle2,
      color: '#22c55e',
      duration: 1000
    }
  ];

  const [currentStage, setCurrentStage] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // Auto-progress through stages
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          if (currentStage < stages.length - 1) {
            setCurrentStage(current => current + 1);
            return 0;
          }
          return 100;
        }
        return prev + 2;
      });
    }, stages[currentStage].duration / 50);

    return () => clearInterval(interval);
  }, [currentStage]);

  const current = stages[currentStage];
  const Icon = current.icon;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '400px',
      padding: '40px',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%)',
      borderRadius: '12px',
      position: 'relative'
    }}>
      {/* Main Animation Container */}
      <div style={{ position: 'relative', marginBottom: '40px' }}>
        {/* Floating Words Animation */}
        <div style={{
          position: 'absolute',
          width: '300px',
          height: '300px',
          left: '-110px',
          top: '-110px',
          pointerEvents: 'none'
        }}>
          {['Simplify', 'Clarify', 'Condense', 'Streamline'].map((word, i) => (
            <motion.div
              key={word}
              style={{
                position: 'absolute',
                fontSize: '12px',
                fontWeight: '600',
                color: current.color,
                opacity: 0.3
              }}
              animate={{
                x: [0, 120 * Math.cos(i * Math.PI / 2), 0],
                y: [0, 120 * Math.sin(i * Math.PI / 2), 0],
                opacity: [0.1, 0.4, 0.1]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                delay: i * 1
              }}
            >
              {word}
            </motion.div>
          ))}
        </div>

        {/* Central Icon with Glow Effect */}
        <motion.div
          style={{
            width: '80px',
            height: '80px',
            background: 'white',
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 10px 40px ${current.color}30`,
            position: 'relative',
            zIndex: 10
          }}
          animate={{
            scale: [1, 1.05, 1],
            boxShadow: [
              `0 10px 40px ${current.color}30`,
              `0 15px 50px ${current.color}50`,
              `0 10px 40px ${current.color}30`
            ]
          }}
          transition={{
            duration: 2,
            repeat: currentStage === stages.length - 1 ? 0 : Infinity
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={current.id}
              initial={{ scale: 0, rotate: -180, opacity: 0 }}
              animate={{ scale: 1, rotate: 0, opacity: 1 }}
              exit={{ scale: 0, rotate: 180, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Icon style={{ 
                width: '40px', 
                height: '40px', 
                color: current.color 
              }} />
            </motion.div>
          </AnimatePresence>
        </motion.div>

        {/* Pulse Rings */}
        {currentStage < stages.length - 1 && (
          <>
            <motion.div
              style={{
                position: 'absolute',
                width: '80px',
                height: '80px',
                border: `2px solid ${current.color}`,
                borderRadius: '20px',
                left: 0,
                top: 0,
                zIndex: 5
              }}
              animate={{
                scale: [1, 1.4, 1.4],
                opacity: [0.5, 0, 0]
              }}
              transition={{
                duration: 2,
                repeat: Infinity
              }}
            />
            <motion.div
              style={{
                position: 'absolute',
                width: '80px',
                height: '80px',
                border: `2px solid ${current.color}`,
                borderRadius: '20px',
                left: 0,
                top: 0,
                zIndex: 4
              }}
              animate={{
                scale: [1, 1.6, 1.6],
                opacity: [0.3, 0, 0]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                delay: 0.5
              }}
            />
          </>
        )}
      </div>

      {/* Stage Info */}
      <motion.div
        key={current.id}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        style={{ textAlign: 'center', marginBottom: '24px' }}
      >
        <h3 style={{
          fontSize: '20px',
          fontWeight: '700',
          color: '#1f2937',
          marginBottom: '8px',
          fontFamily: 'Playfair Display, serif'
        }}>
          {current.title}
        </h3>
        <p style={{
          fontSize: '14px',
          color: '#6b7280'
        }}>
          {current.subtitle}
        </p>
      </motion.div>

      {/* Progress Bar */}
      <div style={{
        width: '300px',
        height: '8px',
        background: '#e5e7eb',
        borderRadius: '4px',
        overflow: 'hidden',
        marginBottom: '16px'
      }}>
        <motion.div
          style={{
            height: '100%',
            background: `linear-gradient(90deg, ${current.color} 0%, ${current.color}dd 100%)`,
            borderRadius: '4px',
            position: 'relative'
          }}
          animate={{
            width: `${progress}%`
          }}
          transition={{
            duration: 0.1
          }}
        >
          {/* Shimmer Effect */}
          <motion.div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
              backgroundSize: '200% 100%'
            }}
            animate={{
              backgroundPosition: ['200% 0', '-200% 0']
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'linear'
            }}
          />
        </motion.div>
      </div>

      {/* Stage Indicators */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginTop: '24px'
      }}>
        {stages.slice(0, -1).map((stage, index) => (
          <motion.div
            key={stage.id}
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: index < currentStage ? stage.color :
                        index === currentStage ? stage.color :
                        '#e5e7eb',
              opacity: index <= currentStage ? 1 : 0.5,
              transition: 'all 0.3s ease'
            }}
            animate={index === currentStage ? {
              scale: [1, 1.3, 1]
            } : {}}
            transition={{
              duration: 1,
              repeat: Infinity
            }}
          />
        ))}
      </div>

      {/* Text Transformation Visualization */}
      <div style={{
        position: 'absolute',
        bottom: '20px',
        left: '20px',
        right: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        opacity: 0.3,
        pointerEvents: 'none'
      }}>
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            style={{
              width: `${60 - i * 5}px`,
              height: '3px',
              background: current.color,
              borderRadius: '2px'
            }}
            animate={{
              scaleX: [1, 0.5, 1],
              opacity: [0.3, 0.6, 0.3]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              delay: i * 0.1
            }}
          />
        ))}
      </div>

      {/* Particles Effect */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        borderRadius: '12px'
      }}>
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            style={{
              position: 'absolute',
              width: '4px',
              height: '4px',
              background: current.color,
              borderRadius: '50%',
              left: `${10 + i * 15}%`
            }}
            animate={{
              y: [450, -50],
              x: [0, (Math.random() - 0.5) * 100],
              opacity: [0, 1, 0]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              delay: i * 0.7,
              ease: 'easeOut'
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default RewriteGenerationAnimation;